import { Box, ButtonBase } from '@mui/material';
import { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ReactComponent as MenuFilledIcon } from 'assets/MenuFilledIcon.svg';
import { ReactComponent as MenuMobileIcon } from 'assets/MenuMobileIcon.svg';
import { ReactComponent as FilledHomeIcon } from 'assets/FilledHomeIcon.svg';
import { ReactComponent as HomeUnfilledIcon } from 'assets/HomeUnfilledIcon.svg';
import { ReactComponent as TransactionsFilledIcon } from 'assets/TransactionsFilledIcon.svg';
import { ReactComponent as TransactionsMobileIcon } from 'assets/TransactionsMobileIcon.svg';
import { ReactComponent as ReimbursementFilledIcon } from 'assets/ReimbursementFilledIcon.svg';
import { ReactComponent as ReimbursementUnfilledIcon } from 'assets/ReimbursementUnfilledIcon.svg';
import { NAV_ROUTES, NAV_ICON_SIZE } from 'app/pages/mobile/utils/constants';
import { useIsEnabled } from 'app/shared-components/feature-flags/FeatureFlagProvider';
import { useAppSelector } from 'app/store';
import { selectUserCompanies } from 'app/store/userCompaniesSlice';

const MobileNavBar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { 'reimbursementsFlags.showReimbursements': showReimbursements } = useIsEnabled([
    { featureName: 'reimbursementsFlags', flagName: 'showReimbursements' },
  ]);
  const { selectedPaidol } = useAppSelector(selectUserCompanies);

  // Mirror web: reimbursements show only when the flag is on AND the paidol has
  // enabled them in App Settings (Paidol.reimbursementSettings.isEnabled).
  const isReimbursementsEnabled = !!showReimbursements && !!selectedPaidol?.reimbursementSettings?.isEnabled;

  const [activeTab, setActiveTab] = useState<'home' | 'transactions' | 'reimbursements' | 'menu'>('home');

  const hiddenPrefixes = [
    '/card/',
    '/mobile-page',
    '/mobile-signin',
    '/mobile-forgot-password',
    '/mobile-reset-password',
    '/mobile-new-password',
    '/review',
  ];

  const shouldHideNavBar = hiddenPrefixes.some((prefix) => location.pathname.startsWith(prefix));

  // Filter navigation items based on feature flag
  const navItems = useMemo(() => {
    const items = [
      { key: 'home', label: 'Home', FilledIcon: FilledHomeIcon, UnfilledIcon: HomeUnfilledIcon },
      {
        key: 'transactions',
        label: 'Transactions',
        FilledIcon: TransactionsFilledIcon,
        UnfilledIcon: TransactionsMobileIcon,
      },
      {
        key: 'reimbursements',
        label: 'Reimbursements',
        FilledIcon: ReimbursementFilledIcon,
        UnfilledIcon: ReimbursementUnfilledIcon,
      },
      { key: 'menu', label: 'Menu', FilledIcon: MenuFilledIcon, UnfilledIcon: MenuMobileIcon },
    ];

    // Filter out reimbursements if feature flag is disabled
    if (!isReimbursementsEnabled) {
      return items.filter((item) => item.key !== 'reimbursements');
    }

    return items;
  }, [isReimbursementsEnabled]);

  // Adjust gap based on number of items: 48px for 3 items (original), 24px for 4 items
  const gap = navItems.length === 3 ? '48px' : '24px';

  // Update active tab based on current route
  useEffect(() => {
    const path = location.pathname;
    if (path.includes('/mobile-demo') || path.includes('/home')) {
      setActiveTab('home');
    } else if (path.includes('/transactions')) {
      setActiveTab('transactions');
    } else if (path.includes('/reimbursements') && isReimbursementsEnabled) {
      setActiveTab('reimbursements');
    } else if (path.includes('/settings') || path.includes('/menu')) {
      setActiveTab('menu');
    }
  }, [location.pathname, isReimbursementsEnabled]);

  // Reset activeTab if it's 'reimbursements' but feature flag is disabled
  useEffect(() => {
    if (activeTab === 'reimbursements' && !isReimbursementsEnabled) {
      // Default to home if reimbursements tab is active but flag is off
      setActiveTab('home');
    }
  }, [activeTab, isReimbursementsEnabled]);

  if (shouldHideNavBar) {
    return null;
  }

  const handleTabClick = (tab: keyof typeof NAV_ROUTES) => {
    setActiveTab(tab);
    navigate(NAV_ROUTES[tab]);
  };

  return (
    <Box
      sx={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        gap: gap,
        pt: 2,
        px: 2,
        pb: 3,
        //height: '72px', // Fixed height for the nav content
        borderTop: 1,
        borderColor: 'grey.200',
        bgcolor: 'background.paper',
      }}
    >
      {navItems.map(({ key, label, FilledIcon, UnfilledIcon }) => (
        <ButtonBase
          key={key}
          onClick={() => handleTabClick(key as keyof typeof NAV_ROUTES)}
          focusRipple={false}
          sx={{
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            p: 0,
            WebkitTapHighlightColor: 'transparent',
            '&:focus-visible': (theme) => ({
              outline: `2px solid ${theme.palette.primary.main}`,
              outlineOffset: 4,
            }),
          }}
          aria-label={label}
          aria-current={activeTab === key ? 'page' : undefined}
        >
          {activeTab === key ? (
            <FilledIcon style={{ height: NAV_ICON_SIZE, width: 'auto' }} />
          ) : (
            <UnfilledIcon style={{ height: NAV_ICON_SIZE, width: 'auto' }} />
          )}
        </ButtonBase>
      ))}
    </Box>
  );
};

export default MobileNavBar;
